import { useContext, useEffect, useState } from "react";
import {
    Box, Button, Card, Chip, CircularProgress, Divider, FormControl, FormLabel,
    Grid, Input, Modal, ModalDialog, Option, Select, Sheet, Stack, Tab,
    TabList, TabPanel, Tabs, Typography, Alert,
} from "@mui/joy";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

import { usePartner } from "../../PartnerContext";
import {
    APIGetPartnerBalance,
    APIGetPartnerEarnings,
    APIGetPartnerPayouts,
    APIRequestPartnerPayout,
} from "../../api_partner";


const STATUS_COLORS = {
    pending: 'warning',
    available: 'success',
    paid_out: 'primary',
    cancelled: 'danger',
    requested: 'warning',
    in_progress: 'primary',
    paid: 'success',
    rejected: 'danger',
};

const STATUS_LABELS = {
    pending: 'Ожидание',
    available: 'Доступно',
    paid_out: 'Выплачено',
    cancelled: 'Отменено',
    requested: 'Запрошена',
    in_progress: 'В обработке',
    paid: 'Выплачена',
    rejected: 'Отклонена',
};

const SOURCE_LABELS = {
    invoice: 'Платёж',
    bonus: 'Бонус',
    manual: 'Ручное',
};


function PayoutRequestModal({ open, onClose, maxAmount, onSuccess }) {
    const { partnerToken: token } = usePartner();
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('card');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const submit = async () => {
        setError(null);
        const numAmount = parseFloat(amount);
        if (!numAmount || numAmount <= 0) {
            setError('Введите корректную сумму');
            return;
        }
        try {
            setSubmitting(true);
            await APIRequestPartnerPayout(token, { amount: numAmount, method });
            onSuccess?.();
            onClose();
            setAmount('');
        } catch (e) {
            setError(e?.response?.data?.error || 'Ошибка запроса выплаты');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ minWidth: 320 }}>
                <Typography level="title-lg">Запрос выплаты</Typography>
                <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
                    Доступно к выводу: <b>{maxAmount?.toLocaleString('ru-RU')} ₽</b>
                </Typography>

                {error && <Alert color="danger" sx={{ mb: 1 }}>{error}</Alert>}

                <Stack spacing={1.5}>
                    <FormControl>
                        <FormLabel>Сумма (₽)</FormLabel>
                        <Input
                            type="number"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            placeholder="0"
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Способ</FormLabel>
                        <Select value={method} onChange={(_, v) => setMethod(v)}>
                            <Option value="card">На карту</Option>
                            <Option value="sbp">СБП</Option>
                            <Option value="bank_account">На расчётный счёт (юрлицо/ИП)</Option>
                            <Option value="manual">Ручной перевод</Option>
                        </Select>
                    </FormControl>

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button variant="outlined" color="neutral" onClick={onClose} fullWidth>
                            Отмена
                        </Button>
                        <Button onClick={submit} loading={submitting} fullWidth>
                            Запросить
                        </Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


export default function PartnerBalance() {
    const { partnerToken: token } = usePartner();
    const [balance, setBalance] = useState(null);
    const [earnings, setEarnings] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [innerTab, setInnerTab] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const [b, e, p] = await Promise.all([
                APIGetPartnerBalance(token),
                APIGetPartnerEarnings(token, { pageSize: 30 }),
                APIGetPartnerPayouts(token),
            ]);
            setBalance(b);
            setEarnings(e?.results || []);
            setPayouts(p?.results || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) load();
    }, [token]);

    if (loading) {
        return (
            <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
            {/* Карточки баланса */}
            <Grid container spacing={1.5}>
                <Grid xs={12} md={4}>
                    <Card variant="solid" color="primary" sx={{ p: 2 }}>
                        <Typography level="body-xs" sx={{ color: 'common.white', opacity: 0.85 }}>
                            Доступно к выводу
                        </Typography>
                        <Typography level="h2" sx={{ color: 'common.white' }}>
                            {(balance?.balance ?? 0).toLocaleString('ru-RU')} ₽
                        </Typography>
                        <Button
                            variant="solid"
                            color="neutral"
                            sx={{ mt: 1, bgcolor: 'common.white', color: 'primary.700' }}
                            onClick={() => setModalOpen(true)}
                            disabled={(balance?.balance ?? 0) <= 0}
                        >
                            Запросить выплату
                        </Button>
                    </Card>
                </Grid>
                <Grid xs={6} md={4}>
                    <Card variant="soft" color="success" sx={{ p: 2 }}>
                        <Typography level="body-xs">Всего заработано</Typography>
                        <Typography level="h3">
                            {(balance?.total_earned ?? 0).toLocaleString('ru-RU')} ₽
                        </Typography>
                    </Card>
                </Grid>
                <Grid xs={6} md={4}>
                    <Card variant="soft" sx={{ p: 2 }}>
                        <Typography level="body-xs">Уже выплачено</Typography>
                        <Typography level="h3">
                            {(balance?.total_paid_out ?? 0).toLocaleString('ru-RU')} ₽
                        </Typography>
                        <Chip size="sm" variant="outlined" sx={{ mt: 0.5, alignSelf: 'flex-start' }}>
                            Комиссия {balance?.commission_percent}%
                        </Chip>
                    </Card>
                </Grid>
            </Grid>

            {/* Вкладки: начисления / выплаты */}
            <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)}>
                <TabList>
                    <Tab>Начисления ({earnings.length})</Tab>
                    <Tab>Выплаты ({payouts.length})</Tab>
                </TabList>

                <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
                    <Stack spacing={1}>
                        {earnings.length === 0 && (
                            <Typography level="body-sm" color="neutral">Начислений пока нет</Typography>
                        )}
                        {earnings.map(e => (
                            <Sheet key={e.id} variant="outlined" sx={{ p: 1.5, borderRadius: 'sm' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography level="title-sm">
                                            +{Number(e.amount).toLocaleString('ru-RU')} ₽
                                        </Typography>
                                        <Typography level="body-xs" color="neutral">
                                            {SOURCE_LABELS[e.source] || e.source}
                                            {e.invoice_item_type && ` • ${e.invoice_item_type}`}
                                            {' • '}
                                            {format(new Date(e.date_create), 'd MMM yyyy, HH:mm', { locale: ru })}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        size="sm"
                                        color={STATUS_COLORS[e.status] || 'neutral'}
                                        variant="soft"
                                    >
                                        {STATUS_LABELS[e.status] || e.status}
                                    </Chip>
                                </Stack>
                            </Sheet>
                        ))}
                    </Stack>
                </TabPanel>

                <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
                    <Stack spacing={1}>
                        {payouts.length === 0 && (
                            <Typography level="body-sm" color="neutral">Выплат пока не было</Typography>
                        )}
                        {payouts.map(p => (
                            <Sheet key={p.id} variant="outlined" sx={{ p: 1.5, borderRadius: 'sm' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography level="title-sm">
                                            {Number(p.amount).toLocaleString('ru-RU')} ₽
                                        </Typography>
                                        <Typography level="body-xs" color="neutral">
                                            {p.method === 'card' && p.card_last4 && `**** ${p.card_last4} • `}
                                            {p.method === 'sbp' && 'СБП • '}
                                            {p.method === 'bank_account' && p.account_last4 && `Счёт **** ${p.account_last4} • `}
                                            {format(new Date(p.requested_at), 'd MMM yyyy, HH:mm', { locale: ru })}
                                        </Typography>
                                        {p.comment && (
                                            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                                {p.comment}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Chip
                                        size="sm"
                                        color={STATUS_COLORS[p.status] || 'neutral'}
                                        variant="soft"
                                    >
                                        {STATUS_LABELS[p.status] || p.status}
                                    </Chip>
                                </Stack>
                            </Sheet>
                        ))}
                    </Stack>
                </TabPanel>
            </Tabs>

            <PayoutRequestModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                maxAmount={balance?.balance}
                onSuccess={load}
            />
        </Stack>
    );
}
